import {
  InputHTMLAttributes,
  KeyboardEvent,
  MouseEventHandler,
  ReactElement,
  useEffect,
  useRef,
  useState,
} from 'react';
import { CheckboxStatus } from '../../../enums';
import { joinClassNames } from '../../../utils';
import styles from './styles.module.scss';

export type CheckboxProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'id'> & {
  id: string;
  label: string | ReactElement;
  /**
   * Visually hides the label
   */
  hideLabel?: boolean;
  hasError?: boolean;
  isSubcomponent?: boolean;
  onClick?: MouseEventHandler<HTMLElement>;
  statusCurrent?: CheckboxStatus;
  statusInitial?: CheckboxStatus;
  containerClassName?: string;
  /**
   * Disables the component and prevents button clicks.
   */
  disabled?: boolean;
};

const IconChecked = () => <path className={styles.renderedCheckbox__icon} d="M4 8L6.5 10.5L12 5" />;
const IconIndeterminate = () => (
  <rect className={styles.renderedCheckbox__icon} x="4" y="7" width="8" height="2" />
);

export const Checkbox = ({
  id,
  label,
  hideLabel = false,
  hasError = false,
  isSubcomponent = false,
  onClick,
  statusCurrent,
  statusInitial = CheckboxStatus.Unchecked,
  containerClassName,
  className,
  ...inputAttributes
}: CheckboxProps) => {
  const [checkboxStatus, setCheckboxStatus] = useState(statusInitial);
  const checkboxRef = useRef<HTMLInputElement>(null);

  const { disabled } = inputAttributes;

  const defaultOnClick = () => {
    setCheckboxStatus(
      checkboxStatus === CheckboxStatus.Unchecked ? CheckboxStatus.Checked : CheckboxStatus.Unchecked
    );
  };

  const handleKeyPress = (e: KeyboardEvent) => {
    if (e.key === 'Enter') {
      if (onClick) return onClick;
      defaultOnClick();
    }
    return null;
  };

  useEffect(() => {
    if (statusCurrent) {
      setCheckboxStatus(statusCurrent);
    }

    if (checkboxRef.current === null) {
      return;
    }

    checkboxRef.current.checked = false;
    checkboxRef.current.indeterminate = false;

    switch (checkboxStatus) {
      case CheckboxStatus.Checked:
        checkboxRef.current.checked = true;
        break;
      case CheckboxStatus.Indeterminate:
        checkboxRef.current.indeterminate = true;
        break;
      default:
      // Do nothing
    }
  }, [statusCurrent, checkboxStatus]);

  return (
    <div
      className={joinClassNames(styles.checkboxContainer, containerClassName)}
      aria-hidden={isSubcomponent}
    >
      <span
        role="none"
        onClick={disabled || isSubcomponent ? undefined : onClick || defaultOnClick}
        onKeyPress={handleKeyPress}
        data-testid="control-span"
        className={
          disabled
            ? joinClassNames(styles.checkboxDisabled, className)
            : joinClassNames(
                styles.checkbox,
                isSubcomponent && styles.isSubcomponent,
                hasError && styles.hasError,
                className
              )
        }
      >
        <input
          id={id}
          name={id}
          type="checkbox"
          ref={checkboxRef}
          className={styles.hiddenCheckbox}
          onKeyPress={handleKeyPress}
          aria-labelledby={`label-${id}`}
          {...inputAttributes}
        />

        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          xmlns="http://www.w3.org/2000/svg"
          className={joinClassNames(styles.renderedCheckbox, styles[checkboxStatus])}
        >
          <title>{checkboxStatus}</title>
          <rect className={styles.renderedCheckbox__base} x="1" y="1" width="14" height="14" rx="1" />
          {checkboxStatus === CheckboxStatus.Checked && <IconChecked />}
          {checkboxStatus === CheckboxStatus.Indeterminate && <IconIndeterminate />}
        </svg>
      </span>
      <label
        id={`label-${id}`}
        htmlFor={id}
        className={joinClassNames(styles.label, isSubcomponent && styles.inheritCursor)}
        hidden={hideLabel}
        onClick={isSubcomponent ? (e) => e.preventDefault() : undefined}
      >
        {label}
      </label>
    </div>
  );
};

Checkbox.displayName = 'Checkbox';
